import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Hourglass, Mail, Send, X } from 'lucide-react';
import {
  adherenteEsperaActivacion,
  enviarAdherente,
  enviarTodosLosAdherentes,
  getEnvioAdherentesJob,
  getUltimasAltasIntegrantes,
  listarAdherentes,
  motivoNoAgregable,
  reenviarInvitacionAdherente,
  REENVIO_INVITACION_MIN_MS,
  type Adherente,
  type AdicionalBis,
  type EnvioBisLoteResumen,
  type ListarAdherentesResp,
  type ReenvioInvitacionBis,
} from '@lib/services/adherentes.service';
import { PARENTESCO_OPCIONES } from '@lib/catalogs';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import { RechazoBinary } from '@components/RechazoBinary';
import { leerRechazoBinary } from '@lib/rechazo-binary';
import { AgregarAdherenteModal } from '@components/AgregarAdherenteModal';
import { UltimasAltas, EstadoBinary } from '@components/UltimasAltas';
import { useAuthStore } from '@lib/store/auth.store';
import { UserRole, isMedicus } from '@lib/types';
import { fechaHora } from '@lib/utils/fecha';
import { capitalizeName } from '@lib/utils/text';

/**
 * Vista de "Adherentes" — legajos en estado `legajo_bis`: grupos que llegaron
 * en un padrón cuyo número ya existía como legajo (en general familiares que
 * vinieron sin su titular). Desde acá se buscan por legajo, se ve quién va a
 * qué titular y se resuelven: completar los datos del adicional → pasa al
 * padrón del titular → entra al legajo.
 */
const PAGE_SIZE = 10;

const PARENTESCO_LABEL: Record<string, string> = Object.fromEntries(
  PARENTESCO_OPCIONES.map((o) => [o.value, o.label]),
);

/**
 * Embudo de Alta Integrantes (mismo estilo que Alta Grupos): sin activar →
 * activados (listos) → enviados a Binary. "Activados" = bis que pasaron el
 * wizard de activación (Fase 2, bisActivacion=validado).
 */
function KpiOverview({ data }: { data: ListarAdherentesResp }) {
  const sinActivar = Math.max(0, data.total - data.totalActivados);
  const stages: Array<{
    key: string;
    titulo: string;
    legajos: number;
    personas?: number;
    hint: string;
    color: string;
    bg: string;
    Icon: typeof Send;
  }> = [
    {
      key: 'sin-activar',
      titulo: 'Sin activar',
      legajos: sinActivar,
      hint: 'Esperan que el titular active el ingreso del nuevo integrante por el link de activación.',
      color: '#b7791f',
      bg: 'rgba(183,121,31,0.1)',
      Icon: Hourglass,
    },
    {
      key: 'activados',
      titulo: 'Activados (listos)',
      legajos: data.totalActivados,
      hint: 'Completaron datos + declaración jurada. Listos para enviar a Binary si el titular ya está dado de alta.',
      color: '#2563eb',
      bg: 'rgba(37,99,235,0.1)',
      Icon: CheckCircle2,
    },
    {
      key: 'enviados',
      titulo: 'Enviados a Binary',
      legajos: data.totalEnviados,
      personas: Math.max(0, data.totalPersonasEnviados - data.totalEnviados),
      hint: 'Ya se enviaron por API. Se integran al titular cuando Binary los procesa.',
      color: '#1a8c3c',
      bg: 'rgba(26,140,60,0.1)',
      Icon: Send,
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
        marginTop: 20,
      }}
    >
      {stages.map((s, i) => (
        <article
          key={s.key}
          className="card"
          style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: `3px solid ${s.color}` }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                display: 'inline-flex',
                width: 40,
                height: 40,
                borderRadius: 12,
                background: s.bg,
                color: s.color,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <s.Icon size={21} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'var(--color-element-low)',
                }}
              >
                Etapa {i + 1}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-element-high)' }}>{s.titulo}</div>
            </div>
          </div>
          <div>
            <span
              style={{
                fontFamily: 'var(--font-switzer)',
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1,
                color: s.color,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {s.legajos.toLocaleString('es-AR')}
            </span>
            <span className="text-element-mid" style={{ fontSize: 13, marginLeft: 6 }}>
              legajos
            </span>
            {s.personas != null && (
              <div className="text-element-mid" style={{ fontSize: 12, marginTop: 4 }}>
                {s.personas.toLocaleString('es-AR')} integrantes
              </div>
            )}
          </div>
          <p className="text-element-mid" style={{ fontSize: 12, marginTop: 'auto', lineHeight: 1.45 }}>
            {s.hint}
          </p>
        </article>
      ))}
    </div>
  );
}

export function AdherentesPage() {
  const [page, setPage] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [incluirEnviados, setIncluirEnviados] = useState(false);
  // VEN-561: filtro "solo los que Binary rechazó" (el motivo se muestra por fila).
  const [soloRechazados, setSoloRechazados] = useState(false);
  const [resolviendo, setResolviendo] = useState<Adherente | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [enviandoId, setEnviandoId] = useState<string | null>(null);
  const [loteAbierto, setLoteAbierto] = useState(false);
  const [reenviandoMail, setReenviandoMail] = useState<Adherente | null>(null);
  const qc = useQueryClient();
  const { user } = useAuthStore();
  /**
   * Reenviar el mail de activación es solo de SUPERADMIN (pedido explícito). El
   * corte de verdad es el `@Roles` de `POST /legajos/bis/:id/reenviar-invitacion`.
   */
  const esSuperAdmin = user?.rol === UserRole.SUPERADMIN_MEDICUS;
  /**
   * Despachar a Binary es solo de Medicus. La organización entra a esta
   * pantalla para VER en qué estado están sus grupos de integrantes y las
   * últimas altas, no para mandarlos. Esconder el botón es cosmética: el corte
   * de verdad está en el backend, donde `POST /legajos/bis/enviar` y
   * `POST /legajos/bis/:id/enviar` siguen siendo ADMIN/SUPERADMIN.
   */
  const puedeEnviarABinary = isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);

  // Debounce de la búsqueda (300ms) — al confirmarse resetea a página 1.
  useEffect(() => {
    const handle = setTimeout(() => {
      setQ(busqueda);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [busqueda]);

  const query = useQuery({
    queryKey: ['adherentes', page, q, incluirEnviados, soloRechazados],
    queryFn: () =>
      listarAdherentes({ page, pageSize: PAGE_SIZE, q, incluirEnviados, soloRechazados }),
    placeholderData: keepPreviousData,
  });

  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / PAGE_SIZE));

  // Bis enviables por API en TODO el scope (no solo la página): destino ya
  // afiliado (dado_de_alta), ACTIVADO por el titular y todavía no marcado
  // enviadoABinary. Las tres condiciones son las que valida el server: si acá
  // faltara la de activación, el botón ofrecería enviar bis que rebotan con
  // BIS_NO_ACTIVADO.
  //
  // El conteo lo hace el SERVER (`totalEnviables`) con ese mismo criterio: antes
  // se contaban los de `items`, que es una página de 10, así que el botón decía
  // "Enviar 10" aunque hubiera cientos esperando y había que paginar a mano.
  const totalEnviables = query.data?.totalEnviables ?? 0;

  async function handleEnviarUno(a: Adherente) {
    setEnviandoId(a.id);
    setExito(null);
    setErrorEnvio(null);
    try {
      const r = await enviarAdherente(a.id);
      if (r.estado === 'ok') {
        const n = r.integrantes?.length ?? 0;
        setExito(
          `${n} integrante${n === 1 ? '' : 's'} dado${n === 1 ? '' : 's'} de alta en Binary y agregado${
            n === 1 ? '' : 's'
          } al legajo ${r.numero ?? a.numero}.`,
        );
      } else if (r.estado === 'omitido') {
        setExito(`El legajo ${r.numero ?? a.numero} ya tenía a sus integrantes en el destino; se limpió el bis.`);
      } else {
        setErrorEnvio(
          `No se pudo enviar el legajo ${a.numero}: ${
            leerRechazoBinary(r.binaryError)?.motivo ?? r.errores?.join(' · ') ?? 'error desconocido'
          }`,
        );
      }
      qc.invalidateQueries({ queryKey: ['adherentes'] });
    } catch (e) {
      setErrorEnvio(mensajeDeError(e));
    } finally {
      setEnviandoId(null);
    }
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Alta Integrantes</h1>
        <p className="text-element-mid">
          Adicionales que llegaron en un padrón cuyo número de legajo ya existe
          (en general, sin su titular). Si el titular <strong>todavía no está en Binary</strong>, se{' '}
          <strong>agregan al legajo</strong> (completando los datos faltantes) y salen al alta junto al titular.
          Si el titular <strong>ya está dado de alta en Binary</strong>, se{' '}
          <strong>envían a Binary por API</strong> (alta de integrantes): se cuelgan del titular y, al transformar,
          se integran al legajo y se borra el bis. Si el titular está en proceso (en validación, validado o en alta)
          quedan como solo informativos hasta que el legajo se libere. Los ya enviados a Binary se ocultan por
          defecto; usá el filtro para incluirlos.
        </p>
      </header>

      {query.data && <KpiOverview data={query.data} />}

      <div className="card" style={{ marginTop: 24 }}>
        <div className="rowBetween" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            className="input"
            style={{ maxWidth: 280 }}
            placeholder="Buscar por número de legajo…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar por número de legajo"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {(query.data?.totalEnviados ?? 0) > 0 && (
              <label
                className="text-element-mid"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={incluirEnviados}
                  onChange={(e) => {
                    setIncluirEnviados(e.target.checked);
                    setPage(1);
                  }}
                />
                Mostrar enviados a Binary ({(query.data?.totalEnviados ?? 0).toLocaleString('es-AR')} legajos
                {' / '}{Math.max(0, (query.data?.totalPersonasEnviados ?? 0) - (query.data?.totalEnviados ?? 0)).toLocaleString('es-AR')} integrantes)
              </label>
            )}
            {((query.data?.totalRechazados ?? 0) > 0 || soloRechazados) && (
              <label
                className="text-element-mid"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={soloRechazados}
                  onChange={(e) => {
                    setSoloRechazados(e.target.checked);
                    setPage(1);
                  }}
                />
                Solo rechazados por Binary ({(query.data?.totalRechazados ?? 0).toLocaleString('es-AR')})
              </label>
            )}
            <h2 className="heading-5" style={{ margin: 0 }}>
              {(query.data?.total ?? 0).toLocaleString('es-AR')} legajos{' '}
              <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-element-mid)' }}>
                ({Math.max(0, (query.data?.totalPersonas ?? 0) - (query.data?.total ?? 0)).toLocaleString('es-AR')} integrantes)
              </span>
            </h2>
          </div>
        </div>

        {puedeEnviarABinary && totalEnviables > 0 && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-primary btn--sm"
              onClick={() => {
                setExito(null);
                setErrorEnvio(null);
                setLoteAbierto(true);
              }}
              title="Envía a Binary por API TODOS los grupos de integrantes listos (titular ya afiliado y activado por él), no solo los de esta página. Respeta la búsqueda que tengas puesta."
            >
              <Send size={14} /> Enviar {totalEnviables.toLocaleString('es-AR')} a Binary
            </button>
          </div>
        )}

        {exito && (
          <p className="status status-success" style={{ display: 'block', marginTop: 12 }}>
            {exito}
          </p>
        )}
        {errorEnvio && (
          <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
            {errorEnvio}
          </p>
        )}

        {query.isLoading && (
          <div style={{ marginTop: 16 }}>
            <TableSkeleton rows={8} />
          </div>
        )}
        {query.isError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 16 }}>
            No se pudieron cargar los legajos.
          </p>
        )}
        {query.data && query.data.items.length === 0 && (
          <p className="text-element-mid" style={{ marginTop: 16 }}>
            {q
              ? `No hay integrantes a sumar que coincidan con "${q}".`
              : 'No hay integrantes pendientes de sumar.'}
          </p>
        )}

        {query.data && query.data.items.length > 0 && (
          <div className="tableWrap" style={{ marginTop: 16 }}>
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Legajo</th>
                  <th>Adicional(es)</th>
                  <th>Va al grupo de</th>
                  <th>Legajo original</th>
                  <th>Detectado</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((a) => (
                  <AdherenteRow
                    key={a.id}
                    item={a}
                    enviando={enviandoId === a.id}
                    onResolver={() => {
                      setExito(null);
                      setResolviendo(a);
                    }}
                    onEnviar={() => handleEnviarUno(a)}
                    puedeEnviarABinary={puedeEnviarABinary}
                    puedeReenviarMail={esSuperAdmin}
                    onReenviarMail={() => {
                      setExito(null);
                      setErrorEnvio(null);
                      setReenviandoMail(a);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {query.data && query.data.total > PAGE_SIZE && (
          <footer
            className="rowBetween"
            style={{ alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--color-border, #E2E8F0)' }}
          >
            <span className="text-element-mid" style={{ fontSize: 12 }}>
              Mostrando {(page - 1) * PAGE_SIZE + 1}-
              {Math.min(page * PAGE_SIZE, query.data.total)} de {query.data.total} · Página{' '}
              {page} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </footer>
        )}
      </div>

      <UltimasAltas
        queryKey="ultimas-altas-integrantes"
        fetcher={getUltimasAltasIntegrantes}
        columnas={['Fecha y hora', 'Legajo', 'Integrante', 'DNI', 'Parentesco', 'MEDICARD', 'Estado', 'Plan actual']}
        claveFila={(a) => a.asociadoId}
        fila={(a) => [
          fechaHora(a.altaAt),
          a.numero,
          capitalizeName(a.integrante),
          a.documento,
          PARENTESCO_OPCIONES.find((p) => p.value === a.parentesco)?.label ?? a.parentesco,
          a.medicard ?? '—',
          <EstadoBinary estado={a.estadoBinary} />,
          a.planActual ?? '—',
        ]}
        vacio="Todavía no se sumó ningún integrante a Binary desde esta pantalla."
      />

      {resolviendo && (
        <AgregarAdherenteModal
          adherente={resolviendo}
          onClose={() => setResolviendo(null)}
          onSuccess={(n) => {
            setExito(
              `${n} integrante${n === 1 ? '' : 's'} agregado${n === 1 ? '' : 's'} al legajo ${resolviendo.numero}.`,
            );
            qc.invalidateQueries({ queryKey: ['adherentes'] });
          }}
        />
      )}

      {loteAbierto && puedeEnviarABinary && (
        <EnviarBisLoteModal
          cantidad={totalEnviables}
          iniciarEnvio={() => enviarTodosLosAdherentes(q)}
          onCerrar={() => setLoteAbierto(false)}
          onProgreso={() => qc.invalidateQueries({ queryKey: ['adherentes'] })}
        />
      )}

      {reenviandoMail && esSuperAdmin && (
        <ReenviarMailModal
          item={reenviandoMail}
          onCerrar={() => setReenviandoMail(null)}
          onEnviado={(r) => {
            setReenviandoMail(null);
            setExito(`Mail de activación reenviado a ${r.enviadoA} (legajo ${r.numero}).`);
            qc.invalidateQueries({ queryKey: ['adherentes'] });
          }}
        />
      )}
    </section>
  );
}

function mensajeDeError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message ?? err?.message ?? 'No se pudo completar el envío.';
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 37, 100, 0.4)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1050,
};

/* ─────────────────── Modal envío en lote de bis a Binary ─────────────────── */

function EnviarBisLoteModal(props: {
  /** Cuántos se van a enviar (para los labels). */
  cantidad: number;
  /** Dispara el job en el backend y devuelve el resumen con el jobId. */
  iniciarEnvio: () => Promise<EnvioBisLoteResumen>;
  onCerrar: () => void;
  onProgreso: () => void;
}) {
  const { cantidad, iniciarEnvio, onCerrar, onProgreso } = props;
  const [jobId, setJobId] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);
  const [errorInicio, setErrorInicio] = useState<string | null>(null);

  const jobQ = useQuery({
    queryKey: ['envio-bis-job', jobId],
    queryFn: () => getEnvioAdherentesJob(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const st = query.state.data?.estado;
      return st === 'terminado' || st === 'error' ? false : 2000;
    },
  });
  const resumen = jobQ.data;
  const enCurso = !!jobId && resumen?.estado !== 'terminado' && resumen?.estado !== 'error';

  useEffect(() => {
    if (resumen) onProgreso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen?.procesados, resumen?.estado]);

  async function iniciar() {
    setIniciando(true);
    setErrorInicio(null);
    try {
      const r = await iniciarEnvio();
      setJobId(r.jobId);
    } catch (e) {
      setErrorInicio(mensajeDeError(e));
    } finally {
      setIniciando(false);
    }
  }

  return (
    <div
      style={OVERLAY_STYLE}
      onClick={(e) => {
        if (!enCurso && e.target === e.currentTarget) onCerrar();
      }}
    >
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 640, width: '100%', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '85vh' }}
      >
        <header className="rowBetween" style={{ alignItems: 'center' }}>
          <h3 className="heading-5" style={{ margin: 0 }}>
            Enviar integrantes a Binary
          </h3>
          {!enCurso && (
            <button type="button" className="modalClose" onClick={onCerrar} aria-label="Cerrar">
              <X size={18} />
            </button>
          )}
        </header>

        {!jobId && (
          <>
            <p style={{ margin: 0 }}>
              Vas a enviar <strong>{cantidad}</strong> grupo(s) de integrantes a Binary por API. Cada
              uno cuelga sus adicionales del titular ya afiliado; los que transforman se integran al legajo y se borra
              el bis.
            </p>
            {errorInicio && (
              <p className="status status-error" style={{ padding: '8px 12px' }}>
                {errorInicio}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-ghost" onClick={onCerrar} disabled={iniciando}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => iniciar()} disabled={iniciando}>
                {iniciando ? (
                  <>
                    <span className="spinner" /> Iniciando…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Enviar {cantidad}
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {jobId && <BisLoteProgreso resumen={resumen} cargando={jobQ.isLoading} enCurso={enCurso} onCerrar={onCerrar} />}
      </div>
    </div>
  );
}

function BisLoteProgreso(props: {
  resumen?: EnvioBisLoteResumen;
  cargando: boolean;
  enCurso: boolean;
  onCerrar: () => void;
}) {
  const { resumen, cargando, enCurso, onCerrar } = props;
  if (!resumen) {
    return <p className="text-element-mid">{cargando ? 'Cargando…' : 'Sin datos del envío.'}</p>;
  }
  const pct = resumen.totalGrupos > 0 ? Math.round((resumen.procesados / resumen.totalGrupos) * 100) : 0;
  const fallidos = (resumen.resultados ?? []).filter((r) => r.estado === 'error');
  return (
    <>
      <div>
        <div className="rowBetween" style={{ fontSize: 13, marginBottom: 6 }}>
          <span>
            {enCurso ? 'Enviando…' : 'Terminado'} — {resumen.procesados} / {resumen.totalGrupos}
          </span>
          <span>{pct}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 6, background: 'var(--color-surface-accent)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: enCurso ? '#7C3AED' : '#166534', transition: 'width .3s' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
        <span style={{ color: '#166534' }}>
          <strong>{resumen.exitosos}</strong> integrados
        </span>
        <span style={{ color: '#B91C1C' }}>
          <strong>{resumen.fallidos}</strong> rechazados
        </span>
        <span className="text-element-mid">
          <strong>{resumen.omitidos}</strong> ya estaban
        </span>
      </div>
      {resumen.estado === 'error' && resumen.error && (
        <p className="status status-error" style={{ padding: '8px 12px' }}>
          El envío abortó: {resumen.error}
        </p>
      )}
      {fallidos.length > 0 && (
        <div className="tableWrap" style={{ overflow: 'auto', maxHeight: 260 }}>
          <table className="dataTable">
            <thead>
              <tr>
                <th>Legajo</th>
                <th>Titular</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {fallidos.map((r, i) => (
                <tr key={`${r.legajo}-${i}`}>
                  <td>
                    <strong>{r.legajo}</strong>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.titular.apellido} {r.titular.nombres}
                  </td>
                  <td style={{ fontSize: 12 }}>{r.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary" onClick={onCerrar} disabled={enCurso}>
          {enCurso ? (
            <>
              <span className="spinner" /> Procesando…
            </>
          ) : (
            'Cerrar'
          )}
        </button>
      </div>
    </>
  );
}

/** "Quién": nombre + parentesco + aviso de datos a completar del adicional. */
function AdicionalChip({ adicional }: { adicional: AdicionalBis }) {
  const nombre =
    `${adicional.apellSolt} ${adicional.nombres}`.trim() || adicional.asociadoId;
  const parentesco = PARENTESCO_LABEL[adicional.parentesco] ?? adicional.parentesco ?? '—';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span>{nombre}</span>
      <span className="text-element-mid" style={{ fontSize: 11 }}>
        ({parentesco}
        {adicional.documento ? ` · DNI ${adicional.documento}` : ''})
      </span>
      {adicional.camposConError.length > 0 && (
        <span
          className="status status-warning"
          style={{ fontSize: 11 }}
          title={`Datos a completar: ${adicional.camposConError.join(', ')}`}
        >
          {adicional.camposConError.length} dato
          {adicional.camposConError.length === 1 ? '' : 's'} a completar
        </span>
      )}
    </div>
  );
}

function AdherenteRow({
  item,
  enviando,
  onResolver,
  onEnviar,
  puedeEnviarABinary,
  puedeReenviarMail,
  onReenviarMail,
}: {
  item: Adherente;
  enviando: boolean;
  onResolver: () => void;
  onEnviar: () => void;
  /** false (organización): en vez del botón de envío se muestra el estado. */
  puedeEnviarABinary: boolean;
  /** Solo SUPERADMIN: estado del mail de activación + "Reenviar mail". */
  puedeReenviarMail: boolean;
  onReenviarMail: () => void;
}) {
  // Estado VIVO del destino (el snapshot de legajoBisOrigen puede ser viejo).
  const estadoDestino = item.estadoDestino || item.legajoBisOrigen?.estadoOriginal;
  const motivo = motivoNoAgregable(item);
  // Destino ya afiliado pero el titular todavía no activó por el wizard: el
  // envío rebotaría con BIS_NO_ACTIVADO, así que no ofrecemos el botón.
  const esperaActivacion = adherenteEsperaActivacion(item);

  return (
    <tr>
      <td>
        <strong>{item.numero}</strong>
      </td>
      <td>
        <div style={{ display: 'grid', gap: 4 }}>
          {item.adicionales.map((ad) => (
            <AdicionalChip key={ad.asociadoId} adicional={ad} />
          ))}
        </div>
      </td>
      <td>
        {item.titularApellido} {item.titularNombre}
        <div className="text-element-mid" style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          DNI {item.titularDocumento}
        </div>
      </td>
      <td style={{ fontSize: 12 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {item.legajoBisOrigen?.numero ?? '—'}{' '}
          {estadoDestino ? <EstadoLegajoBadge estado={estadoDestino} size={11} /> : null}
        </div>
      </td>
      <td style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
        {item.legajoBisOrigen?.detectadoAt
          ? new Date(item.legajoBisOrigen.detectadoAt).toLocaleString('es-AR')
          : '—'}
      </td>
      <td>
        {/* VEN-561: el último rebote de Binary, visible en la fila. El bis sigue
            pendiente y el botón de enviar queda disponible para reintentar. La
            pastilla es corta y el motivo va abajo; solicitud, fecha e intentos,
            en el tooltip. */}
        {!item.enviadoABinary && item.binaryError && (
          <div style={{ marginBottom: 6 }}>
            <RechazoBinary
              mensaje={item.binaryError.message}
              at={item.binaryError.at}
              intentos={item.binaryError.intentos}
              conPastilla
              detalleEnLinea={false}
            />
          </div>
        )}
        {item.enviadoABinary ? (
          <span
            className="status status-success"
            style={{ fontSize: 12 }}
            title={`Enviado a Binary el ${new Date(item.enviadoABinary.at).toLocaleString('es-AR')}${
              item.enviadoABinary.batch ? ` · lote ${item.enviadoABinary.batch}` : ''
            }. Se vincula solo al titular cuando Binary active al adicional.`}
          >
            ✓ Enviado a Binary
          </span>
        ) : (
          <>
            {motivo === null && (
              <button type="button" className="btn btn-primary btn--sm" onClick={onResolver}>
                Agregar al legajo
              </button>
            )}
            {motivo === 'en_proceso' && (
              <span
                className="text-element-mid"
                style={{ fontSize: 12 }}
                title="El titular está en proceso (en validación, validado o en alta); no se puede agregar ahora."
              >
                Solo informativo
              </span>
            )}
            {/* VEN-615: con un adherente (parentesco 13) el envío se rechaza
                siempre antes de llegar a Binary y el server ya no lo cuenta
                como enviable. Se muestra el bloqueo y no la acción: lo único
                que destraba es quitar al adherente del legajo. */}
            {motivo === 'dado_de_alta' && item.tieneAdherente && (
              <span
                className="status status-warning"
                style={{ fontSize: 12 }}
                title="El parentesco 13 (Adherente) no está habilitado en esta etapa: Binary no lo acepta y no se reintenta. Hay que quitar al adherente del legajo (desde Observaciones) para poder enviar al resto."
              >
                Adherente no habilitado · quitar del legajo
              </span>
            )}
            {motivo === 'dado_de_alta' && !item.tieneAdherente && esperaActivacion && (
              <>
                <span
                  className="text-element-mid"
                  style={{ fontSize: 12 }}
                  title="El titular todavía no entró al link de activación: falta que confirme los datos del nuevo integrante y complete la declaración jurada. Hasta entonces Binary rechaza el envío."
                >
                  Espera activación del titular
                </span>
                {puedeReenviarMail && <MailActivacion item={item} onReenviar={onReenviarMail} />}
              </>
            )}
            {motivo === 'dado_de_alta' && !item.tieneAdherente && !esperaActivacion && puedeEnviarABinary && (
              <button
                type="button"
                className="btn btn-primary btn--sm"
                onClick={onEnviar}
                disabled={enviando}
                title="El titular ya está afiliado en Binary: envía estos integrantes por API (alta de integrantes), se cuelgan del titular y se integran al legajo."
              >
                {enviando ? (
                  <>
                    <span className="spinner" /> Enviando…
                  </>
                ) : (
                  <>
                    <Send size={14} /> Enviar a Binary
                  </>
                )}
              </button>
            )}
            {motivo === 'dado_de_alta' && !item.tieneAdherente && !esperaActivacion && !puedeEnviarABinary && (
              // La organización ve el estado, no la acción: el grupo ya cumplió
              // todo lo que depende de ella (titular afiliado + activación
              // hecha) y queda esperando que Medicus lo despache.
              <span
                className="status status-success"
                style={{ fontSize: 12 }}
                title="El titular ya está afiliado y el grupo fue activado: queda listo para que Medicus lo envíe a Binary."
              >
                Listo para el alta
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * Estado del mail de activación, solo para SUPERADMIN:
 *  - padrón sin validar: el mail no salió y no se reenvía, sale al validarlo
 *    (el caso que más confunde: "no le llegó" cuando nunca se mandó);
 *  - enviado: cuándo y a qué dirección; si el titular ya entró al link
 *    (activación en curso), también se dice: es la prueba de que le llegó;
 *  - padrón validado sin envío registrado: el titular no tenía email en su
 *    momento o el servicio de mails lo rechazó. Se puede mandar desde acá.
 */
function MailActivacion({ item, onReenviar }: { item: Adherente; onReenviar: () => void }) {
  // Un server anterior a VEN-573 no manda `padronValidado`: sin el dato no se
  // dice nada, en vez de afirmar que el padrón no está validado.
  if (item.padronValidado === undefined) return null;
  if (!item.padronValidado) {
    return (
      <div
        className="text-element-mid"
        style={{ fontSize: 11, marginTop: 4 }}
        title="El mail de activación sale al validar el padrón que trajo a este integrante. Validalo desde Padrones."
      >
        Mail sin enviar: el padrón no está validado
      </div>
    );
  }
  const envio = ultimoEnvio(item);
  const reciente = envioReciente(item);
  return (
    <div style={{ display: 'grid', gap: 4, marginTop: 4, justifyItems: 'start' }}>
      <span className="text-element-mid" style={{ fontSize: 11 }}>
        {envio ? `Mail enviado el ${envio}` : 'Sin mail enviado'}
      </span>
      {item.activacionEstado === 'en_validacion' && (
        <span className="text-element-mid" style={{ fontSize: 11 }}>
          El titular ya entró al link y está completando los datos.
        </span>
      )}
      <button
        type="button"
        className="btn btn-ghost btn--sm"
        onClick={onReenviar}
        disabled={reciente}
        aria-label={`Reenviar mail del legajo ${item.numero}`}
        title={reciente ? 'Salió hace menos de 2 minutos: esperá un poco antes de reenviarlo.' : undefined}
      >
        <Mail size={14} /> Reenviar mail
      </button>
    </div>
  );
}

/** "21/9/2026 13:26 a titular@x.com" del último mail de activación, o null si nunca salió. */
function ultimoEnvio(item: Adherente): string | null {
  if (!item.invitacionEnviadaAt) return null;
  const destino = item.invitacionEnviadaA ? ` a ${item.invitacionEnviadaA}` : '';
  return `${fechaHora(item.invitacionEnviadaAt)}${destino}`;
}

/** Dentro de la ventana en la que el server rechaza otro envío (REENVIO_MUY_SEGUIDO). */
function envioReciente(item: Adherente): boolean {
  if (!item.invitacionEnviadaAt) return false;
  return Date.now() - Date.parse(item.invitacionEnviadaAt) < REENVIO_INVITACION_MIN_MS;
}

function integrantesTexto(item: Adherente): string {
  const n = item.familiaresAsociadoIds.length;
  if (n === 1) return 'su nuevo integrante';
  return n > 1 ? `sus ${n} nuevos integrantes` : 'sus nuevos integrantes';
}

/* ─────────────────── Modal reenvío del mail de activación ─────────────────── */

/** Tras abrir, un click en el fondo no cierra: cubre la entrada y el doble click. */
const CLICK_FONDO_IGNORADO_MS = 400;

function ReenviarMailModal(props: {
  item: Adherente;
  onCerrar: () => void;
  onEnviado: (r: ReenvioInvitacionBis) => void;
}) {
  const { item, onCerrar, onEnviado } = props;
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abiertoEn = useRef(Date.now());
  const envio = ultimoEnvio(item);
  // '' = el titular destino no tiene email: el server lo rechazaría (422).
  const sinEmail = item.emailTitularDestino === '';

  async function reenviar() {
    setEnviando(true);
    setError(null);
    let r: ReenvioInvitacionBis;
    try {
      r = await reenviarInvitacionAdherente(item.id);
    } catch (e) {
      setError(mensajeDeError(e));
      setEnviando(false);
      return;
    }
    // Fuera del try: si algo falla después, no se muestra como un envío fallido
    // (el mail ya salió y reintentar mandaría otro).
    setEnviando(false);
    onEnviado(r);
  }

  return (
    // Fondo y cuadro con transición de entrada (`.modalBackdrop` + `.modalEntrada`,
    // globals.css) para que el modal no aparezca de golpe. `.modalCard` como los
    // demás confirms: tope de alto con scroll, así los botones no quedan afuera.
    <div
      className="modalBackdrop"
      onClick={(e) => {
        if (enviando || e.target !== e.currentTarget) return;
        // El segundo click de un doble click en "Reenviar mail" cae sobre este
        // fondo recién montado y cerraría el modal antes de que se vea.
        if (Date.now() - abiertoEn.current < CLICK_FONDO_IGNORADO_MS) return;
        onCerrar();
      }}
    >
      <div
        className="modalCard modalEntrada"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reenviar-mail-titulo"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !enviando) onCerrar();
        }}
        style={{ maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <header className="rowBetween" style={{ alignItems: 'center' }}>
          <h3 id="reenviar-mail-titulo" className="heading-5" style={{ margin: 0 }}>
            Reenviar mail de activación
          </h3>
          {!enviando && (
            <button type="button" className="modalClose" onClick={onCerrar} aria-label="Cerrar">
              <X size={18} />
            </button>
          )}
        </header>
        <p style={{ margin: 0 }}>
          A <strong>{capitalizeName(`${item.titularApellido} ${item.titularNombre}`)}</strong> (legajo{' '}
          {item.numero}) le vuelve a llegar el mail con el link para activar a {integrantesTexto(item)}.
        </p>
        <DestinoDelMail email={item.emailTitularDestino} />
        <p className="text-element-mid" style={{ margin: 0, fontSize: 13 }}>
          {envio ? `Último envío: ${envio}.` : 'No hay ningún envío registrado para este legajo.'}
        </p>
        {error && (
          <p className="status status-error" style={{ padding: '8px 12px', margin: 0 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          {/* Foco inicial en Cancelar: con Enter no se manda un mail por accidente. */}
          <button type="button" className="btn btn-ghost" onClick={onCerrar} disabled={enviando} autoFocus>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => reenviar()}
            disabled={enviando || sinEmail}
          >
            {enviando ? (
              <>
                <span className="spinner" /> Enviando…
              </>
            ) : (
              <>
                <Mail size={16} /> Reenviar mail
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A dónde va el mail: el email que tiene HOY el titular del legajo destino.
 * Es otro registro que el titular de la fila cuando el padrón nuevo lo trajo,
 * así que es el que hay que chequear con el socio.
 */
function DestinoDelMail({ email }: { email?: string }) {
  if (email === undefined) {
    return (
      <p style={{ margin: 0 }}>
        Va al email que el titular tiene cargado <strong>hoy</strong>.
      </p>
    );
  }
  if (email === '') {
    return (
      <p className="status status-warning" style={{ padding: '8px 12px', margin: 0 }}>
        El titular no tiene un email cargado. Corregilo en su ficha antes de reenviar.
      </p>
    );
  }
  return (
    <p style={{ margin: 0 }}>
      Se envía a <strong>{email}</strong>, el email que tiene cargado hoy el titular.
    </p>
  );
}
